import { Outlet, createRootRoute, useLocation } from '@tanstack/react-router'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { Suspense, useEffect, useState } from 'react'
import { LogisticMeansPortalAppbar } from '../components/Appbar/LogisticMeansPortalAppbar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import { LogisticMeansPortalSidebar } from '../components/Appbar/LogisticMeansPortalSidebar'
import { useOktaAuth } from '@okta/okta-react'
import { toRelativeUrl } from '@okta/okta-auth-js'
import { LoadingPage } from '@floriday/floriday-ui'
import { alpha } from '@mui/material/styles'
import { useCollectingFormContext } from '../context/collectingContext'
import { useUser } from '../hooks/useUser'
import { BusinessAssociationBanner } from '../components/BusinessAssociationBanner'

const EnsureAuth = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { oktaAuth, authState } = useOktaAuth()

  useEffect(() => {
    const enforceAuth = async () => {
      if (location.pathname === '/callback') {
        return
      }

      if (!authState) {
        // still initializing, effect will run again once authState is set
        return
      }

      if (!authState.isAuthenticated) {
        await oktaAuth.signInWithRedirect({
          originalUri: toRelativeUrl(
            window.location.href,
            window.location.origin
          ),
        })
      }
    }
    enforceAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, authState])

  return <>{children}</>
}

const RootComponent = () => {
  const [open, setOpen] = useState(false)
  const { isSubmitting, selectedBusinessAssociation } =
    useCollectingFormContext()
  const { customerNumber } = useUser()
  const isOwnUse = selectedBusinessAssociation?.id === customerNumber

  return (
    <>
      <EnsureAuth>
        {isSubmitting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme => theme.zIndex.modal + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
            }}
          >
            <LoadingPage />
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <LogisticMeansPortalAppbar setOpen={setOpen} />
          <Box
            sx={{
              display: 'flex',
              paddingTop: theme => theme.spacing(9.25),
              flexGrow: 1,
            }}
          >
            <LogisticMeansPortalSidebar open={open} setOpen={setOpen} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
            >
              <BusinessAssociationBanner
                businessAssociationName={selectedBusinessAssociation?.name}
                isOwnUse={isOwnUse}
              />
              <Container
                maxWidth='lg'
                sx={{
                  py: theme => theme.spacing(2),
                }}
              >
                <Outlet />
              </Container>
            </Box>
          </Box>
        </Box>
      </EnsureAuth>
      <Suspense>
        <ReactQueryDevtools />
        <TanStackRouterDevtools />
      </Suspense>
    </>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
})
